<template>
  <div class="excel-entry-manager">
    <h2>Excel Entries</h2>
    <div class="actions">
      <router-link to="/excel/descriptions">
        <el-button type="primary">
          {{ t('excel.browser.viewHistory') }}
        </el-button>
      </router-link>
    </div>
    
    <!-- Health Status -->
    <el-alert
      v-if="!isHealthy"
      type="error"
      :closable="false"
      show-icon
    >
      <template #title>
        Backend Service Unavailable
        <el-button
          type="primary"
          size="small"
          @click="checkHealth"
          :loading="isChecking"
          style="margin-left: 10px"
        >
          Retry Connection
        </el-button>
      </template>
      <template #default>
        Last check: {{ lastCheckTime ? new Date(lastCheckTime).toLocaleString() : 'Never' }}
      </template>
    </el-alert>

    <el-alert
      v-else
      type="success"
      :closable="false"
      show-icon
    >
      <template #title>
        Backend Service Available
      </template>
      <template #default>
        Last check: {{ lastCheckTime ? new Date(lastCheckTime).toLocaleString() : 'Never' }}
      </template>
    </el-alert>

    <!-- Date Picker -->
    <el-date-picker
      v-model="selectedDate"
      type="date"
      placeholder="Select Date"
      @change="adjustDate"
      :disabled="!isHealthy"
      class="date-picker"
    />

    <el-table 
      :data="entries" 
      style="width: 100%"
      :stripe="true"
      :border="true"
      class="entry-table"
    >
      <el-table-column prop="id" label="ID" width="120" fixed="left">
        <template #default="{ row }">
          <span class="id-cell">{{ row.id }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="descricao" label="Description" min-width="300"/>
      <el-table-column prop="dt" label="Date" width="150">
        <template #default="{ row }">
          {{ new Date(row.dt).toLocaleDateString() }}
        </template>
      </el-table-column>
      <el-table-column prop="valor" label="Value" width="150" align="right">
        <template #default="{ row }">
          {{ row.valor.toFixed(2) }}
        </template>
      </el-table-column>
      <el-table-column prop="lastchanged" label="Last Changed" width="200">
        <template #default="{ row }">
          {{ new Date(row.lastchanged).toLocaleString() }}
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="220" fixed="right">
        <template #default="{ row }">
          <div class="action-buttons">
            <router-link :to="`/excel/edit/${row.id}`">
              <el-button 
                type="primary" 
                size="small" 
                :disabled="!isHealthy"
              >
                {{ t('excel.actions.edit') }}
              </el-button>
            </router-link>
            
            <el-button 
              type="info"
              size="small" 
              :disabled="!isHealthy"
              @click="showHistory(row.descricao)"
            >
              {{ t('excel.actions.viewHistory') }}
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import type { ExcelEntryDTO } from '../types/accounting'
import Swal from 'sweetalert2'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const authStore = useAuthStore()
const router = useRouter()


// Create axios instance with default config
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + authStore.keycloak?.token
  }
})


const entries = ref<ExcelEntryDTO[]>([])
const selectedDate = ref<Date | null>(null)
const isHealthy = ref(false)
const isChecking = ref(false)
const lastCheckTime = ref<number | null>(null)
const showingHistory = ref(false)
const selectedDescription = ref<string>('')

const fetchLatestDate = async () => {
  if (!isHealthy.value) return

  try {
    const response = await api.get<string>(
      `${import.meta.env.VITE_EXCEL_API_URL}/excel/findLatestDate`
    )
    selectedDate.value = new Date(response.data)
    fetchEntries(selectedDate.value.toISOString().split('T')[0])
  } catch (err: any) {
    console.error('Failed to fetch latest date:', err)
    Swal.fire({
      title: 'Error',
      text: 'Failed to fetch latest date',
      icon: 'error'
    })
  }
}

const checkHealth = async () => {
  isChecking.value = true
  try {
    await api.get(`${import.meta.env.VITE_EXCEL_API_URL}/excel/health`)
    isHealthy.value = true
    lastCheckTime.value = Date.now()
    if (!selectedDate.value) {
      await fetchLatestDate()
    }
  } catch (error) {
    isHealthy.value = false
    console.error('Health check failed:', error)
  } finally {
    isChecking.value = false
  }
}

// Watch for health status changes to fetch latest date when service becomes available
watch(() => isHealthy.value, async (newValue) => {
  if (newValue && !selectedDate.value) {
    await fetchLatestDate()
  }
})

const adjustDate = (date: Date) => {
  if (!isHealthy.value) return

  const selectedMonth = date.getMonth()
  const selectedYear = date.getFullYear()
  const fifteenth = new Date(selectedYear, selectedMonth, 15)
  const lastDay = new Date(selectedYear, selectedMonth + 1, 0)

  if (date.getDate() <= 15) {
    selectedDate.value = fifteenth
  } else {
    selectedDate.value = lastDay
  }

  fetchEntries(selectedDate.value.toISOString().split('T')[0])
}

const fetchEntries = async (dt: string) => {
  if (!isHealthy.value) return

  const url = import.meta.env.VITE_EXCEL_API_URL + '/excel/findByDt/' + dt
  console.log(url)
  try {
    const response = await api.get<ExcelEntryDTO[]>(url)
    entries.value = response.data
  } catch (error: any) {
    console.error('Failed to fetch entries:', error)
    const errorMessage = error.response?.data?.message || 'Failed to fetch entries'
    if (error.response?.status === 401 || error.response?.status === 403) {
      Swal.fire({
        title: 'Authentication Error',
        text: 'Please check your authentication credentials.',
        icon: 'error'
      })
    } else {
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      })
    }
  }
}

const showHistory = (descricao: string) => {
  selectedDescription.value = descricao
  showingHistory.value = true
  router.push({ path: '/excel/history', query: { descricao } })
}

onMounted(checkHealth)
</script>

<style scoped>
.excel-entry-manager {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.entry-table {
  margin-top: 20px;
}

.date-picker {
  margin: 20px 0;
  width: 200px;
}

.id-cell {
  font-family: monospace;
  font-size: 1.1em;
}

.el-alert {
  margin-bottom: 1rem;
}

.el-alert :deep(.el-alert__title) {
  display: flex;
  align-items: center;
}

.actions {
  margin: 20px 0;
}

.action-buttons {
  display: flex;
  gap: 8px;
}
</style> 